import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import omit from 'lodash.omit';
import { i18next, utils } from '@yola/ws-sdk';
import LinkListSettingsDialog from './link-list-settings-dialog';
import constants from '../constants';

const {
  common: { ENTER_KEYCODE },
} = constants;

const defaultItems = [];
const defaultInitialItems = [];

const LinkListSettingsDialogContainer = ({
  items: originalItems = defaultItems,
  initialItems = defaultInitialItems,
  offsetX,
  offsetY,
  dialogHeight = 472,
  dialogWidth = 336,
  modalContentClassName = '',
  onOpenChangeItemDialog = utils.noop,
  onSubmit = utils.noop,
  onClose = utils.noop,
  captions,
  maxNestingLevel = 3,
  handleDeleteItem = utils.noop,
  handleAddNewItem = utils.noop,
  handleEditItem = utils.noop,
  handleOrderChange = utils.noop,
}) => {
  const modalRef = useRef();
  const [items, setItems] = useState(originalItems);

  const deleteItem = useCallback(
    (id) => {
      const { order } = items.find((item) => item.id === id);
      const updatedItems = items.filter((item) =>
        maxNestingLevel === 1 ? item.order !== order : !item.order.startsWith(order)
      );
      setItems(updatedItems);
      handleDeleteItem();
    },
    [items, maxNestingLevel, handleDeleteItem]
  );

  const openChangeItemDialog = useCallback(
    (item = null) => {
      const {
        state: { offsetX: x, offsetY: y },
      } = modalRef.current;

      onOpenChangeItemDialog({ items, item, offsetX: x, offsetY: y });
    },
    [onOpenChangeItemDialog, items]
  );

  const createActionsHandlers = useCallback(
    (oldItems) =>
      oldItems.map((item) => {
        const settingsAction = {
          label: i18next.t('Settings'),
          glyph: 'settings',
          onClick: () => {
            handleEditItem();
            openChangeItemDialog(item);
          },
        };

        const deleteAction = {
          label: i18next.t('Delete'),
          glyph: 'trash',
          onClick: () => {
            deleteItem(item.id);
          },
        };

        const actions = [settingsAction, deleteAction];

        return {
          ...item,
          actions,
          iconGlyph: 'drag-icon',
          iconType: 'light',
          droppable: true,
        };
      }),
    [deleteItem, openChangeItemDialog, handleEditItem]
  );

  const applyChanges = (event) => {
    if (!items.length) {
      onClose();
      return;
    }
    const filteredItems = Object.values(omit(items, ['actions']));
    onSubmit({ items, event, isItemsChanged: !isEqual(filteredItems, initialItems) });
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === ENTER_KEYCODE) {
      applyChanges(e);
    }
  };

  const itemsWithActionHandlers = createActionsHandlers(items);

  return (
    <LinkListSettingsDialog
      modalRef={modalRef}
      items={itemsWithActionHandlers}
      handleKeyDown={handleKeyDown}
      applyChanges={applyChanges}
      onClose={onClose}
      modalContentClassName={modalContentClassName}
      onOrderChange={(updatedItems) => {
        handleOrderChange();
        setItems(updatedItems);
      }}
      maxNestingLevel={maxNestingLevel}
      dialogWidth={dialogWidth}
      dialogHeight={dialogHeight}
      offsetX={offsetX}
      offsetY={offsetY}
      captions={captions}
      onAddNewItem={() => {
        handleAddNewItem();
        openChangeItemDialog();
      }}
    />
  );
};

LinkListSettingsDialogContainer.propTypes = {
  offsetX: PropTypes.number.isRequired,
  offsetY: PropTypes.number.isRequired,
  modalContentClassName: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  initialItems: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  onOpenChangeItemDialog: PropTypes.func,
  dialogHeight: PropTypes.number,
  dialogWidth: PropTypes.number,
  maxNestingLevel: PropTypes.number,
  captions: PropTypes.shape({
    dialogTitle: PropTypes.string,
    emptyTitle: PropTypes.string,
    emptyDescription: PropTypes.string,
    submit: PropTypes.string,
    cancel: PropTypes.string,
  }).isRequired,
  handleDeleteItem: PropTypes.func,
  handleAddNewItem: PropTypes.func,
  handleEditItem: PropTypes.func,
  handleOrderChange: PropTypes.func,
};

export default LinkListSettingsDialogContainer;
